"use client";

import { useQuery } from "@tanstack/react-query";
import { LogIn, Menu, Megaphone, Search, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState, type FocusEvent, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";

import { AnnouncementMenu, CommandPalette, NotificationCenter } from "@/components/globals";
import { TenantSwitcher } from "@/components/layout/tenant-switcher";
import { WorkspaceScopeFilter } from "@/components/layout/workspace-scope-filter";
import { UserAccountMenu } from "@/components/layout/user-account-menu";
import { WorkspaceMobileNavigation, WorkspacePrimarySidebar } from "@/components/layout/workspace-primary-sidebar";
import { WorkspaceStickyTitle } from "@/components/layout/workspace-sticky-title";
import { AppLayout, Banner, IconButton, Tooltip } from "@/components/ui-system";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { FlashToast } from "@/components/ui-system/flash-toast";
import { listAnnouncements, type AnnouncementList } from "@/lib/api/endpoints/announcements";
import {
  getQuotaStatus,
  getSuperAdminLockedWorkspaceSummary,
  type StorageQuotaStatusDto,
  type SuperAdminLockedWorkspaceSummaryDto,
} from "@/lib/api/endpoints/data-management";
import { listDashboards } from "@/lib/api/endpoints/dashboards";
import { listTenants } from "@/lib/api/endpoints/tenants";
import { getBootstrapRealtimeUrl } from "@/lib/mock/flags";
import { consumeRouteFlash } from "@/lib/navigation/route-flash";
import { filterSidebarGroupsForUser, type SidebarNavGroup } from "@/lib/navigation/sidebar-nav";
import { hasModulePermission, useEffectivePermissions } from "@/modules/auth/access";
import { isSuperAdmin } from "@/modules/auth/permissions";
import { useAuthStore } from "@/modules/auth/store";
import { RealtimeBootstrap } from "@/modules/devices/components/realtime-bootstrap";
import { useCommandPaletteStore } from "@/store/command-palette";
import { useWorkspaceChromeStore } from "@/store/workspace-chrome";

const SIDEBAR_PINNED_KEY = "atech-workspace-sidebar-pinned";

function isImmersiveCanvasRoute(pathname: string): boolean {
  return /^\/dashboards\/[^/]+$/.test(pathname);
}

/**
 * The header workspace switcher is operational context, not a permanent
 * navigation fixture. Keep it off routes that manage platform-wide records or
 * the user's own account. Form-level workspace fields (for example, an
 * announcement audience) remain available because they describe the record
 * being edited rather than the page's active scope.
 */
function routeUsesWorkspaceContext(pathname: string, isPlatformActor: boolean): boolean {
  if (
    pathname === "/access-pending" ||
    pathname === "/overview" ||
    pathname === "/devices/new" ||
    pathname === "/profile" ||
    pathname === "/billing" ||
    pathname === "/security" ||
    pathname === "/system-health" ||
    pathname === "/tenants" ||
    pathname === "/platform-users" ||
    pathname === "/platform-roles" ||
    pathname === "/device-categories" ||
    pathname === "/explorer" ||
    pathname === "/live" ||
    pathname === "/analytics" ||
    pathname === "/expense-categories" ||
    pathname === "/notifications" ||
    pathname.startsWith("/announcements")
  ) {
    return false;
  }

  // Platform data-management is fleet-wide; customer data-management remains
  // scoped to the current workspace and therefore retains the selector.
  if (pathname.startsWith("/data-management") && isPlatformActor) {
    return false;
  }

  return true;
}

function dashboardShortLabel(name: string): string {
  return name.trim().split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase() ?? "").join("") || "DB";
}

export function EnterpriseAppChrome({ children }: { children: ReactNode }) {
  const { pathname, search } = useLocation();
  const user = useAuthStore((state) => state.user);
  const tenantId = useAuthStore((state) => state.tenantId);
  const impersonationStartedAt = useAuthStore((state) => state.impersonation?.started_at ?? null);
  const permissions = useEffectivePermissions();
  const togglePalette = useCommandPaletteStore((state) => state.toggle);
  const headerSearch = useWorkspaceChromeStore((state) => state.search);
  const headerAction = useWorkspaceChromeStore((state) => state.headerAction);
  const platformUser = user?.accountType === "platform";
  const superAdmin = isSuperAdmin(user);
  const showCustomerWorkspaceSwitcher = Boolean(user) && !superAdmin && !platformUser;
  const showPlatformWorkspaceSwitcher = superAdmin || platformUser;
  // On a device's view/edit page, the device selector is the relevant context
  // for platform accounts, so do not compete with it in the app header.
  const isDeviceViewOrEditRoute = /^\/devices\/[0-9a-f-]{36}(?:\/edit)?$/i.test(pathname);
  const routeHasWorkspaceContext = routeUsesWorkspaceContext(pathname, superAdmin || platformUser);
  const showGlobalWorkspaceSwitcher = routeHasWorkspaceContext && !(showPlatformWorkspaceSwitcher && isDeviceViewOrEditRoute);
  const currentPath = `${pathname}${search}`;
  const wsUrl = getBootstrapRealtimeUrl();
  const dashboardNavEnabled = Boolean(user?.role && user.role !== "super_admin" && !platformUser && hasModulePermission(user, permissions.permissionMap, "dashboards", "view"));
  const canViewDataManagement = Boolean(user?.role && hasModulePermission(user, permissions.permissionMap, "data_management", "view"));
  const workspaceBrandingQuery = useQuery({
    queryKey: ["tenant-memberships", user?.id ?? ""],
    queryFn: listTenants,
    enabled: Boolean(user) && !superAdmin && !platformUser,
    staleTime: 60_000,
  });
  const activeWorkspaceBranding = (workspaceBrandingQuery.data ?? []).find((workspace) => workspace.id === tenantId);
  const sidebarCompanyName = !superAdmin && !platformUser ? activeWorkspaceBranding?.company_name ?? activeWorkspaceBranding?.name ?? null : null;

  const dashboardNavQuery = useQuery({
    queryKey: ["dashboards", tenantId ?? "", user?.id ?? "anon", user?.role ?? "no-role", impersonationStartedAt ?? "direct", "accessible"],
    queryFn: () => listDashboards(tenantId!, "accessible"),
    enabled: dashboardNavEnabled && Boolean(tenantId),
    staleTime: 60_000,
  });
  const quotaStatusQuery = useQuery<StorageQuotaStatusDto>({
    queryKey: ["data-management", "quota-status", tenantId ?? "none"],
    queryFn: () => getQuotaStatus(tenantId),
    enabled: canViewDataManagement && Boolean(tenantId) && !superAdmin && !platformUser,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
  const superAdminQuotaSummaryQuery = useQuery<SuperAdminLockedWorkspaceSummaryDto>({
    queryKey: ["data-management", "super-admin", "locked-workspaces"],
    queryFn: () => getSuperAdminLockedWorkspaceSummary(),
    enabled: superAdmin || platformUser,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
  const announcementsQuery = useQuery<AnnouncementList>({
    queryKey: ["announcements", "header", user?.id ?? "anon", tenantId ?? "none"],
    queryFn: () => listAnnouncements("audience", { limit: 8 }),
    enabled: Boolean(user) && !superAdmin,
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const baseGroups = useMemo(
    () => filterSidebarGroupsForUser(user, permissions.isSuccess ? permissions.permissionMap : undefined),
    [permissions.isSuccess, permissions.permissionMap, user],
  );
  const groups = useMemo<SidebarNavGroup[]>(() => {
    const dashboards = dashboardNavQuery.data ?? [];
    if (!dashboardNavEnabled || dashboards.length === 0) return baseGroups;
    return baseGroups.map((group) => group.id !== "operations" ? group : {
      ...group,
      items: group.items.flatMap((item) => item.moduleKey !== "overview" ? [item] : [
        item,
        ...dashboards.map((dashboard) => ({
          ...item,
          href: `/overview?dashboard=${encodeURIComponent(dashboard.id)}`,
          label: dashboard.name,
          short: dashboardShortLabel(dashboard.name),
        })),
      ]),
    });
  }, [baseGroups, dashboardNavEnabled, dashboardNavQuery.data]);

  const [sidebarPinned, setSidebarPinned] = useState(false);
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const [sidebarFocusWithin, setSidebarFocusWithin] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [routeFlash, setRouteFlash] = useState<string | null>(null);
  const [quotaRibbonDismissed, setQuotaRibbonDismissed] = useState(false);
  const [dismissedAnnouncementIds, setDismissedAnnouncementIds] = useState<string[]>([]);

  useEffect(() => {
    try { setSidebarPinned(localStorage.getItem(SIDEBAR_PINNED_KEY) === "1"); } catch { /* unavailable storage */ }
  }, []);
  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_PINNED_KEY, sidebarPinned ? "1" : "0"); } catch { /* unavailable storage */ }
  }, [sidebarPinned]);
  useEffect(() => setRouteFlash(consumeRouteFlash()), [pathname]);
  useEffect(() => {
    if (!user?.id) {
      setDismissedAnnouncementIds([]);
      return;
    }
    try {
      const stored = sessionStorage.getItem(`announcement-banner-dismissed:${user.id}`);
      setDismissedAnnouncementIds(stored ? JSON.parse(stored) : []);
    } catch {
      setDismissedAnnouncementIds([]);
    }
  }, [user?.id]);
  useEffect(() => {
    setQuotaRibbonDismissed(false);
  }, [
    tenantId,
    quotaStatusQuery.data?.quota_locked,
    quotaStatusQuery.data?.usage_percent,
    superAdminQuotaSummaryQuery.data?.locked_workspace_count,
    superAdminQuotaSummaryQuery.data?.items.map((item) => item.tenant_id).join(","),
  ]);

  const sidebarExpanded = sidebarPinned || sidebarHovered || sidebarFocusWithin;
  const hideSidebar = isImmersiveCanvasRoute(pathname);
  const announcementItems = announcementsQuery.data?.items ?? [];
  const unreadAnnouncements = announcementsQuery.data?.unread_count ?? 0;
  const lockedWorkspaceSummary = superAdminQuotaSummaryQuery.data;
  const unreadAnnouncementItems = announcementItems.filter((item) => !item.is_read);
  const bannerAnnouncement = unreadAnnouncementItems.find((item) => !dismissedAnnouncementIds.includes(item.id));

  const dismissAnnouncementBanner = useCallback(() => {
    const next = [...new Set([...dismissedAnnouncementIds, ...unreadAnnouncementItems.map((item) => item.id)])];
    setDismissedAnnouncementIds(next);
    if (user?.id) {
      try { sessionStorage.setItem(`announcement-banner-dismissed:${user.id}`, JSON.stringify(next)); } catch { /* unavailable storage */ }
    }
  }, [dismissedAnnouncementIds, unreadAnnouncementItems, user?.id]);
  const onSidebarBlur = useCallback((event: FocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget;
    if (!(next instanceof Node && event.currentTarget.contains(next))) setSidebarFocusWithin(false);
  }, []);
  const collapseAfterNavigate = useCallback(() => {
    setMobileNavOpen(false);
    if (!sidebarPinned) {
      setSidebarHovered(false);
      setSidebarFocusWithin(false);
    }
  }, [sidebarPinned]);

  const sidebar = (
    <WorkspacePrimarySidebar
      currentPath={currentPath}
      groups={groups}
      expanded={sidebarExpanded}
      pinned={sidebarPinned}
      onNavigate={collapseAfterNavigate}
      onTogglePin={() => setSidebarPinned((value) => !value)}
      onFocusContainer={() => setSidebarFocusWithin(true)}
      onBlurContainer={onSidebarBlur}
      companyName={sidebarCompanyName}
    />
  );
  const mobileNav = <WorkspaceMobileNavigation currentPath={currentPath} groups={groups} onNavigate={collapseAfterNavigate} />;

  const header = (
    <>
      {(superAdmin || platformUser) && lockedWorkspaceSummary && lockedWorkspaceSummary.locked_workspace_count > 0 && !quotaRibbonDismissed ? (
        <Banner
          tone="warning"
          title={lockedWorkspaceSummary.locked_workspace_count === 1
            ? `${lockedWorkspaceSummary.items[0]?.admin_name || lockedWorkspaceSummary.items[0]?.tenant_name || "A workspace admin"}'s storage limit was reached`
            : `${lockedWorkspaceSummary.locked_workspace_count} workspaces reached storage limits`}
          description={lockedWorkspaceSummary.items.slice(0, 3).map((item) => item.admin_name || item.tenant_name).filter(Boolean).join(", ")}
          action={<div className="flex items-center gap-ds-2"><Link to="/data-management?status=locked" className="btn btn-secondary btn-sm !no-underline">Review</Link><IconButton variant="ghost" aria-label="Dismiss storage advisory" onClick={() => setQuotaRibbonDismissed(true)}><X className="h-4 w-4" /></IconButton></div>}
        />
      ) : null}
      {quotaStatusQuery.data?.quota_locked && !quotaRibbonDismissed ? (
        <Banner
          tone="error"
          title="Workspace storage limit reached"
          description="Data operations may be restricted until storage is available."
          action={<div className="flex items-center gap-ds-2"><Link to="/data-management" className="btn btn-secondary btn-sm !no-underline">Manage storage</Link><IconButton variant="ghost" aria-label="Dismiss storage limit" onClick={() => setQuotaRibbonDismissed(true)}><X className="h-4 w-4" /></IconButton></div>}
        />
      ) : null}

      <header className="px-ds-3 sm:px-ds-4 md:px-ds-6">
        <div className="flex min-h-[var(--topbar-height)] items-center gap-ds-3">
          {!hideSidebar ? <IconButton variant="ghost" aria-label="Open navigation" className="md:hidden" onClick={() => setMobileNavOpen(true)}><Menu className="h-5 w-5" /></IconButton> : null}
          <div className="min-w-0 flex-1">
            {!isImmersiveCanvasRoute(pathname) ? <WorkspaceStickyTitle pathname={pathname} /> : null}
          </div>
          <div className="flex shrink-0 items-center gap-ds-2 px-ds-2 sm:px-ds-3">
            {showGlobalWorkspaceSwitcher && showPlatformWorkspaceSwitcher ? <WorkspaceScopeFilter includeAllOption className="w-[12rem]" /> : null}
            {showGlobalWorkspaceSwitcher && showCustomerWorkspaceSwitcher ? <TenantSwitcher tenantId={user?.tenantId ?? null} className="shrink-0" /> : null}
            {headerAction ? <div className="shrink-0">{headerAction}</div> : null}
            {!superAdmin ? <AnnouncementMenu items={announcementItems} unread={unreadAnnouncements} loading={announcementsQuery.isLoading} /> : null}
            <Tooltip content="Search workspace (Ctrl/⌘ K)" placement="bottom-end"><IconButton variant="secondary" aria-label="Search workspace" onClick={togglePalette}><Search className="h-4 w-4" /></IconButton></Tooltip>
            <NotificationCenter />
            <ThemeToggle variant="icon" />
            {user ? <UserAccountMenu user={user} /> : <Link to="/login" className="btn btn-secondary btn-sm !no-underline"><LogIn className="h-3.5 w-3.5" />Sign in</Link>}
          </div>
        </div>

        {headerSearch ? (
          <div className="flex min-h-12 items-center gap-ds-2 overflow-x-auto border-t border-card-border py-ds-1.5">
            {headerSearch ? <div className="ml-auto min-w-[14rem] flex-1 sm:max-w-md">{headerSearch}</div> : null}
          </div>
        ) : null}
      </header>

      {!superAdmin && bannerAnnouncement && !pathname.startsWith("/announcements") ? (
        <Banner
          tone="info"
          icon={Megaphone}
          title="New announcement"
          description={bannerAnnouncement.title}
          action={<div className="flex items-center gap-ds-2"><Link to={`/announcements/${bannerAnnouncement.id}`} onClick={dismissAnnouncementBanner} className="text-caption font-semibold text-current underline underline-offset-2">Open</Link><IconButton variant="ghost" aria-label="Dismiss announcement" onClick={dismissAnnouncementBanner}><X className="h-4 w-4" /></IconButton></div>}
        />
      ) : null}
    </>
  );

  return (
    <>
      {wsUrl ? <RealtimeBootstrap wsUrl={wsUrl} /> : null}
      <CommandPalette />
      <AppLayout
        sidebar={sidebar}
        mobileNav={mobileNav}
        header={header}
        hideSidebar={hideSidebar}
        sidebarExpanded={sidebarExpanded}
        sidebarPinned={sidebarPinned}
        mobileNavOpen={mobileNavOpen}
        onMobileNavClose={() => setMobileNavOpen(false)}
        onSidebarPointerEnter={() => setSidebarHovered(true)}
        onSidebarPointerLeave={() => setSidebarHovered(false)}
      >
        {children}
      </AppLayout>
      {routeFlash ? <FlashToast message={routeFlash} onDone={() => setRouteFlash(null)} /> : null}
    </>
  );
}
